export function TermsPage() {
  return (
    <div className="max-w-4xl mx-auto px-4 py-12">
      <h1 className="text-3xl font-display font-bold text-white mb-2">DreamPaths Terms of Use</h1>
      <p className="text-dream-400 text-sm mb-8">Last Updated: 8/22/24</p>

      <div className="card space-y-6">
        <p className="text-dream-300">
          Welcome to DreamPaths, operated by Authentic Creative, LLC (&ldquo;we,&rdquo; &ldquo;us,&rdquo; or
          &ldquo;our&rdquo;). By accessing or using our website, mobile application, or any services provided
          by DreamPaths (collectively, the &ldquo;Service&rdquo;), you agree to be bound by these Terms of Use
          (&ldquo;Terms&rdquo;). If you do not agree to these Terms, please do not use the Service.
        </p>

        <section>
          <h2 className="text-xl font-semibold text-white mb-3">Eligibility</h2>
          <p className="text-dream-300">
            You must be at least 18 years old to use the Service. By using the Service, you represent and
            warrant that you are at least 18 years of age.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-3">Account Creation and Security</h2>
          <p className="text-dream-300">
            To use certain features of the Service, you may need to create an account. You are responsible
            for maintaining the confidentiality of your account credentials and for all activities that occur
            under your account. You agree to notify us immediately of any unauthorized use of your account.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-3">User Content</h2>
          <div className="space-y-3">
            <p className="text-dream-300">
              You retain ownership of any content you submit to the Service, including your dream entries
              (&ldquo;User Content&rdquo;). By submitting User Content, you grant us a worldwide,
              non-exclusive, royalty-free license to use, reproduce, modify, adapt, publish, translate,
              create derivative works from, distribute, and display such User Content in connection with
              providing and promoting the Service.
            </p>
            <p className="text-dream-300">
              You are solely responsible for your User Content and the consequences of posting or publishing
              it. You represent and warrant that you have all necessary rights to submit the User Content and
              that it does not violate any third-party rights or applicable laws.
            </p>
          </div>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-3">Intellectual Property</h2>
          <div className="space-y-3">
            <p className="text-dream-300">
              The Service, including its content, features, and functionality, is owned by Authentic
              Creative, LLC and is protected by copyright, trademark, and other intellectual property laws.
            </p>
            <p className="text-dream-300">
              You are granted a limited, non-exclusive, non-transferable, and revocable license to use the
              Service for personal, non-commercial purposes in accordance with these Terms.
            </p>
            <p className="text-dream-300">
              You retain ownership of your dream entries. We grant you a license to copy and redistribute
              your dream analysis and your usage of the DreamPaths app, provided that such use does not cause
              harm to others, is not illegal, and complies with these Terms.
            </p>
          </div>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-3">Privacy and Data Collection</h2>
          <div className="space-y-3">
            <p className="text-dream-300">
              5.1 Our collection and use of your personal information are governed by our Privacy Policy,
              which is incorporated into these Terms by reference.
            </p>
            <p className="text-dream-300">
              5.2 We collect and process various types of data, including but not limited to: name, email,
              date of birth, IP address, user agent, page views, and product-related data. Dream data is
              stored securely using industry-standard encryption and cloud software.
            </p>
            <p className="text-dream-300">
              5.3 We use third-party services such as Google Analytics, Google Fonts, and third-party APIs
              for LLM and generative AI features. Data is protected by Cloudflare and AWS cloud services.
            </p>
            <p className="text-dream-300">
              5.4 By using the Service, you consent to the collection and use of your data as described in
              our Privacy Policy.
            </p>
          </div>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-3">User Conduct</h2>
          <p className="text-dream-300 mb-3">You agree not to:</p>
          <ol className="list-[lower-alpha] list-inside space-y-2 text-dream-300">
            <li>Use the Service for any unlawful purpose or in violation of these Terms</li>
            <li>
              Impersonate any person or entity or falsely state or misrepresent your affiliation with a
              person or entity
            </li>
            <li>Interfere with or disrupt the Service or servers or networks connected to the Service</li>
            <li>
              Attempt to gain unauthorized access to any portion of the Service or any other systems or
              networks connected to the Service
            </li>
            <li>
              Use any robot, spider, or other automatic device or manual process to monitor or copy any
              content from the Service
            </li>
            <li>Transmit any viruses, worms, defects, Trojan horses, or other items of a destructive nature</li>
            <li>
              Violate our Code of Conduct in community features, direct messages, comments, or other
              interactive areas of the Service
            </li>
          </ol>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-3">Subscriptions and Payments</h2>
          <div className="space-y-3">
            <p className="text-dream-300">
              We offer two subscription plans: &ldquo;Initiate&rdquo; ($11.11/month) and &ldquo;Pro&rdquo;
              ($22.22/month). The Pro plan includes all features of the Initiate plan plus additional
              benefits.
            </p>
            <p className="text-dream-300">We also offer single-time purchase or per-token-purchase features.</p>
            <p className="text-dream-300">
              Subscriptions can be cancelled and refunded within 7 days of payment. Single-use and per-token
              services are non-refundable as they involve the use of GPU time.
            </p>
            <p className="text-dream-300">
              We reserve the right to change our pricing and subscription terms at any time, with notice
              provided to users.
            </p>
          </div>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-3">Termination</h2>
          <p className="text-dream-300">
            We reserve the right to terminate or suspend your account and access to the Service at our sole
            discretion, without notice, for any reason, including but not limited to a breach of these Terms.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-3">Disclaimer of Warranties</h2>
          <p className="text-dream-300">
            THE SERVICE IS PROVIDED &ldquo;AS IS&rdquo; AND &ldquo;AS AVAILABLE&rdquo; WITHOUT ANY WARRANTIES
            OF ANY KIND, EITHER EXPRESS OR IMPLIED. WE DISCLAIM ALL WARRANTIES, INCLUDING BUT NOT LIMITED TO
            THE IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND
            NON-INFRINGEMENT.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-3">
            Not Medical Advice; Entertainment Purposes Only
          </h2>
          <div className="space-y-3">
            <p className="text-dream-300">
              DreamPaths is provided for entertainment purposes only. The Service, including any dream
              analysis, interpretations, or insights provided, does not constitute medical advice, diagnosis,
              or treatment. The Service is not a substitute for professional medical or mental health care.
            </p>
            <p className="text-dream-300">
              If you are experiencing any discomfort, difficulties, or concerns about your physical or mental
              health, you should immediately contact a qualified medical or mental health professional. Do
              not disregard, avoid, or delay obtaining medical or mental health advice from a qualified
              healthcare provider because of something you have read or experienced through the Service.
            </p>
            <p className="text-dream-300">
              By using DreamPaths, you acknowledge and agree that you are using the Service at your own risk
              and for entertainment purposes only.
            </p>
          </div>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-3">Limitation of Liability</h2>
          <p className="text-dream-300">
            TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL AUTHENTIC CREATIVE, LLC OR
            ITS AFFILIATES, OFFICERS, DIRECTORS, EMPLOYEES, OR AGENTS BE LIABLE FOR ANY INDIRECT, PUNITIVE,
            INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR EXEMPLARY DAMAGES, INCLUDING WITHOUT LIMITATION DAMAGES
            FOR LOSS OF PROFITS, GOODWILL, USE, DATA, OR OTHER INTANGIBLE LOSSES, ARISING OUT OF OR RELATING
            TO YOUR USE OF, OR INABILITY TO USE, THE SERVICE.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-3">Indemnification</h2>
          <p className="text-dream-300">
            You agree to indemnify, defend, and hold harmless Authentic Creative, LLC and its officers,
            directors, employees, and agents from and against any and all claims, liabilities, damages,
            losses, or expenses, including reasonable attorneys&rsquo; fees and costs, arising out of or in
            any way connected with your access to or use of the Service or your violation of these Terms.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-3">Governing Law and Dispute Resolution</h2>
          <p className="text-dream-300">
            These Terms shall be governed by and construed in accordance with the laws of the State of
            Delaware, without regard to its conflict of law provisions. Any dispute arising from or relating
            to these Terms or the Service shall be resolved through mediation at a location of
            DreamPaths&rsquo;s choosing. If mediation is unsuccessful, the parties agree to submit to binding
            arbitration in accordance with the rules of the American Arbitration Association.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-3">Changes to Terms</h2>
          <p className="text-dream-300">
            We reserve the right to modify these Terms at any time. We will notify you of any changes by
            posting the new Terms on this page and updating the &ldquo;Last Updated&rdquo; date at the top.
            Your continued use of the Service after any such changes constitutes your acceptance of the new
            Terms.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-3">Miscellaneous</h2>
          <div className="space-y-3">
            <p className="text-dream-300">
              14.1 These Terms constitute the entire agreement between you and Authentic Creative, LLC
              regarding the Service and supersede all prior agreements and understandings.
            </p>
            <p className="text-dream-300">
              14.2 Our failure to enforce any right or provision of these Terms will not be considered a
              waiver of those rights.
            </p>
            <p className="text-dream-300">
              14.3 If any provision of these Terms is held to be invalid or unenforceable, the remaining
              provisions shall remain in full force and effect.
            </p>
            <p className="text-dream-300">
              14.4 You may not assign or transfer these Terms, by operation of law or otherwise, without our
              prior written consent. We may assign or transfer these Terms, at our sole discretion, without
              restriction.
            </p>
          </div>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-3">Contact Us</h2>
          <div className="space-y-3">
            <p className="text-dream-300">
              If you have any questions about these Terms, please contact us at{' '}
              <a href="mailto:support@dreampaths.com" className="text-dream-200 underline hover:text-white">
                support@dreampaths.com
              </a>
              .
            </p>
            <p className="text-dream-300">
              By using the DreamPaths Service, you acknowledge that you have read and understood these Terms
              and agree to be bound by them.
            </p>
          </div>
        </section>
      </div>
    </div>
  );
}
